import React, { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { KpiCard, SearchField } from '../../../components/design-system/admin';
import { CrmPartner, PARTNER_STAGES, PARTNER_TYPES } from '../../../common/models/crm.model';
import { CrmBoard } from './crm-board';
import { CrmCardBody } from './crm-card';
import { CrmField, CrmRecordSheet } from './crm-record-sheet';
import { useCrmBoard } from './use-crm-board';

const PARTNER_FIELDS: CrmField[] = [
  { key: 'name', label: 'ADMIN_CRM.FIELDS.PARTNER_NAME' },
  { key: 'type', label: 'ADMIN_CRM.FIELDS.TYPE', type: 'select', options: PARTNER_TYPES },
  { key: 'contact', label: 'ADMIN_CRM.FIELDS.CONTACT' },
  { key: 'email', label: 'ADMIN_CRM.FIELDS.EMAIL' },
  { key: 'phone', label: 'ADMIN_CRM.FIELDS.PHONE' },
  { key: 'country', label: 'ADMIN_CRM.FIELDS.COUNTRY', type: 'country' },
  { key: 'stage', label: 'ADMIN_CRM.FIELDS.STAGE', type: 'select', options: Object.fromEntries(PARTNER_STAGES.map((s) => [s.id, s.label])) },
  { key: 'zone', label: 'ADMIN_CRM.FIELDS.ZONE', placeholder: 'ADMIN_CRM.PLACEHOLDERS.ZONE' },
  { key: 'network', label: 'ADMIN_CRM.FIELDS.NETWORK', full: true, placeholder: 'ADMIN_CRM.PLACEHOLDERS.NETWORK' },
  { key: 'commission', label: 'ADMIN_CRM.FIELDS.COMMISSION', full: true, placeholder: 'ADMIN_CRM.PLACEHOLDERS.COMMISSION' },
  { key: 'deals', label: 'ADMIN_CRM.FIELDS.DEALS', full: true },
  { key: 'next', label: 'ADMIN_CRM.FIELDS.NEXT' },
  { key: 'due', label: 'ADMIN_CRM.FIELDS.DUE', type: 'date' },
  { key: 'tags', label: 'ADMIN_CRM.FIELDS.TAGS', full: true },
  { key: 'notes', label: 'ADMIN_CRM.FIELDS.NOTES_HISTORY', type: 'textarea' },
];

/** Referral partners (apporteurs d'affaires), as a Kanban like the leads. */
export const CrmPartnersTab: React.FC = () => {
  const { t } = useTranslation();
  const board = useCrmBoard<CrmPartner>('partners', 'ADMIN_CRM.PARTNERS.SAVED');
  const partners = board.items;
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [editing, setEditing] = useState<Partial<CrmPartner> | null>(null);

  const shown = useMemo(() => {
    const needle = q.toLowerCase();
    return partners
      .filter((p) => !type || p.type === type)
      .filter((p) => !needle || JSON.stringify(p).toLowerCase().includes(needle));
  }, [partners, q, type]);

  const live = partners.filter((p) => p.stage !== 'inactive').length;
  const signed = partners.filter((p) => ['agreement', 'active'].includes(p.stage)).length;
  const active = partners.filter((p) => p.stage === 'active').length;
  const producing = partners.filter((p) => (p.deals || '').trim()).length;

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard loading={board.loading} label={t('ADMIN_CRM.PARTNERS.KPI.LIVE')} value={live} hint={t('ADMIN_CRM.PARTNERS.KPI.LIVE_HINT')} />
        <KpiCard loading={board.loading} label={t('ADMIN_CRM.PARTNERS.KPI.SIGNED')} value={<span className="text-success">{signed}</span>} hint={t('ADMIN_CRM.PARTNERS.KPI.SIGNED_HINT')} />
        <KpiCard loading={board.loading} label={t('ADMIN_CRM.PARTNERS.KPI.ACTIVE')} value={<span className="text-success">{active}</span>} hint={t('ADMIN_CRM.PARTNERS.KPI.ACTIVE_HINT')} />
        <KpiCard loading={board.loading} label={t('ADMIN_CRM.PARTNERS.KPI.PRODUCING')} value={<span className="text-warning">{producing}</span>} hint={t('ADMIN_CRM.PARTNERS.KPI.PRODUCING_HINT')} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <SearchField value={q} onChange={setQ} placeholder={t('ADMIN_CRM.PARTNERS.SEARCH')} className="w-full sm:w-80" />
        <NativeSelect value={type} onChange={(e) => setType(e.target.value)} aria-label={t('ADMIN_CRM.COMMON.TYPE')}>
          <NativeSelectOption value="">{t('ADMIN_CRM.COMMON.ALL_TYPES')}</NativeSelectOption>
          {Object.entries(PARTNER_TYPES).map(([k, v]) => <NativeSelectOption key={k} value={k}>{t(v)}</NativeSelectOption>)}
        </NativeSelect>
        <span className="flex-1" />
        <Button onClick={() => setEditing({ stage: 'to_contact', type: 'agence' })}><Plus />{t('ADMIN_CRM.PARTNERS.NEW')}</Button>
      </div>

      {!board.loading && (
        <CrmBoard<CrmPartner>
          stages={PARTNER_STAGES}
          items={shown}
          manualOrder
          onOpen={setEditing}
          onMoveStage={board.moveStage}
          onReorder={board.reorder}
          renderCard={(p) => (
            <CrmCardBody country={p.country} title={p.name} next={p.next} due={p.due} tags={p.tags}>
              <p className="mt-0.5 truncate text-xs text-muted-foreground">
                {PARTNER_TYPES[p.type] ? t(PARTNER_TYPES[p.type]) : p.type}{p.contact ? ` · ${p.contact}` : ''}
              </p>
              {p.zone && <p className="truncate text-xs text-muted-foreground">{p.zone}</p>}
              {p.commission && <p className="mt-1 text-xs font-medium">{p.commission}</p>}
              {p.deals && <p className="mt-0.5 text-xs text-success">→ {p.deals}</p>}
            </CrmCardBody>
          )}
        />
      )}

      <CrmRecordSheet<CrmPartner>
        record={editing}
        fields={PARTNER_FIELDS}
        required="name"
        title={{ create: t('ADMIN_CRM.PARTNERS.CREATE_TITLE'), edit: t('ADMIN_CRM.PARTNERS.EDIT') }}
        onSave={async (d) => { if (await board.save(d)) setEditing(null); }}
        onDelete={async (id) => { if (await board.remove(id)) setEditing(null); }}
        onClose={() => setEditing(null)}
      />
    </div>
  );
};
